import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronRight, RefreshCw, Settings2, Star, Target, CheckCircle2, MessageSquare } from "lucide-react";
import { accountabilityAttention, getCoachScores, runCoachReviews } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CoachDays, Calibrate, ScoreSettings } from "@/pages/CoachScores";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const tone = (v) => (v == null ? "text-zinc-600" : v >= 85 ? "text-emerald-300" : v >= 65 ? "text-amber-300" : "text-red-300");
const pct = (v) => (v == null ? "—" : `${Math.round(v)}%`);

/** What needs doing, coach by coach (overdue touchpoints, unanswered check-ins, quiet clients…).
 *  Coaches see their own list; managers see every coach and work through them one at a time. */
export function NeedsAttention({ onOpenClient, reloadKey }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  const load = useCallback(() => accountabilityAttention().then(setData).catch(() => setData({ coaches: [], total: 0 })), []);
  useEffect(() => { load(); }, [load, reloadKey]);
  if (!data) return null;
  const toggle = (c) => setOpen((s) => { const n = new Set(s); n.has(c) ? n.delete(c) : n.add(c); return n; });
  const withItems = data.coaches.filter((c) => c.count);
  return (
    <section data-testid="needs-attention">
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="h-4 w-4 text-amber-400" />
        <div className="eyebrow">Needs attention</div>
        <span className="text-xs text-zinc-500">{data.total} item{data.total === 1 ? "" : "s"}{data.is_manager ? ` across ${withItems.length} coach${withItems.length === 1 ? "" : "es"}` : ""}</span>
      </div>
      {withItems.length === 0 ? (
        <div className="card-surface p-5 text-sm text-zinc-400 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" />Nothing overdue. Every client is on track.</div>
      ) : (
        <div className="space-y-2">
          {withItems.map((c) => {
            const isOpen = open.has(c.coach) || withItems.length === 1;
            const byClient = c.items.reduce((m, i) => { (m[i.row_id] = m[i.row_id] || { name: i.client_name, thread: i.thread_id, items: [] }).items.push(i); return m; }, {});
            return (
              <div key={c.coach} className="card-surface" data-testid={`attention-${c.coach}`}>
                <button type="button" onClick={() => toggle(c.coach)} aria-expanded={isOpen} className="w-full flex items-center gap-3 px-4 py-3 text-left">
                  {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-500" /> : <ChevronRight className="h-4 w-4 text-zinc-500" />}
                  <span className="font-medium text-zinc-100">{c.coach}</span>
                  <span className="text-xs text-zinc-500">{c.clients} client{c.clients === 1 ? "" : "s"}</span>
                  <span className="ml-auto flex items-center gap-2 text-xs">
                    {c.late > 0 && <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-rose-300">{c.late} late</span>}
                    <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-amber-300">{c.count} to do</span>
                  </span>
                </button>
                {isOpen && (
                  <ul className="border-t border-[#1c1c1c] divide-y divide-[#161616]">
                    {Object.entries(byClient).map(([rowId, cl]) => (
                      <li key={rowId} className="px-4 py-2.5 flex flex-wrap items-start gap-x-4 gap-y-1">
                        <div className="min-w-[9rem] flex flex-col">
                          <button type="button" onClick={() => onOpenClient(rowId)} className="text-left text-sm font-medium text-blue-300 hover:text-blue-200">{cl.name}</button>
                          {cl.thread && <Link to={`/member-app/inbox?thread=${cl.thread}`} className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-200"><MessageSquare className="h-3 w-3" />Open chat</Link>}
                        </div>
                        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
                          {cl.items.map((i, n) => (
                            <span key={n} title={i.detail} className={`rounded-md border px-2 py-0.5 text-xs ${i.severity === "late" ? "border-rose-500/30 bg-rose-500/10 text-rose-200" : "border-amber-500/30 bg-amber-500/10 text-amber-200"}`}>
                              {i.label}{i.detail ? <span className="text-zinc-400"> · {i.detail}</span> : null}
                            </span>
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/** One coach score (touchpoints, client check-ins, replies by 10 am, reply quality, clock-in),
 *  next to how each coach's clients convert. Click a coach for their day-by-day replies. */
export function CoachScoreboard({ stats, user }) {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [calibrate, setCalibrate] = useState(false);
  const [settings, setSettings] = useState(false);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => getCoachScores(month).then(setData).catch(() => setData({ coaches: [], parts: {}, weights: {} })), [month]);
  useEffect(() => { load(); }, [load]);
  const runNow = async () => {
    setRunning(true);
    try {
      const r = await runCoachReviews();
      toast.success(`Checked ${r.reviewed} client-day${r.reviewed === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't run the check");
    } finally {
      setRunning(false);
    }
  };
  if (!data) return <div className="card-surface p-6 text-sm text-zinc-500">Loading…</div>;
  const conv = Object.fromEntries((stats || []).map((s) => [s.coach, s]));
  const names = data.is_manager
    ? [...new Set([...data.coaches.map((c) => c.coach_name), ...(stats || []).map((s) => s.coach)])].filter((n) => n && n !== "Unassigned")
    : data.coaches.map((c) => c.coach_name).concat(conv[user?.name] && !data.coaches.length ? [user.name] : []);
  const score = Object.fromEntries(data.coaches.map((c) => [c.coach_name, c]));
  const rows = names.map((n) => ({ name: n, s: score[n], c: conv[n] }))
    .sort((a, b) => (b.s?.score ?? -1) - (a.s?.score ?? -1) || (b.c?.conversion_pct ?? 0) - (a.c?.conversion_pct ?? 0));
  const w = data.weights || {};
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-zinc-500 max-w-3xl">
          Score out of 100: touchpoints done ({w.touchpoints}%), client check-ins ({w.feedback}%), replies by 10 am ({w.promptness}%),
          reply quality ({w.quality}%) and on time to the gym ({w.punctuality}%). A part with no data yet is left out. Replies are checked
          automatically every morning.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value || thisMonth())} className="w-40 h-8" />
          {data.is_admin && <Button size="sm" variant="outline" onClick={runNow} disabled={running}><RefreshCw className="mr-1 h-3.5 w-3.5" />Check yesterday</Button>}
          {data.is_manager && <Button size="sm" variant="outline" onClick={() => setCalibrate(true)}><Target className="mr-1 h-3.5 w-3.5" />Calibrate</Button>}
          {user?.role === "master_admin" && <Button size="sm" variant="outline" onClick={() => setSettings(true)}><Settings2 className="mr-1 h-3.5 w-3.5" />Scoring</Button>}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="card-surface p-6 text-center text-sm text-zinc-500">No coach data yet. Scores build up as 6 Week Challenge clients start.</div>
      ) : (
        <div className="card-surface overflow-x-auto">
          <table className="w-full text-sm" data-testid="coach-scoreboard">
            <thead className="text-left text-[10px] uppercase tracking-[0.16em] text-zinc-500 bg-[#0d0d0d] border-b border-[#1f1f1f]">
              <tr>
                <th className="px-3 py-2 font-medium">Coach</th>
                <th className="px-3 py-2 font-medium">Score</th>
                <th className="px-3 py-2 font-medium">Touchpoints</th>
                <th className="px-3 py-2 font-medium">Client rating</th>
                <th className="px-3 py-2 font-medium">Replies by 10 am</th>
                <th className="px-3 py-2 font-medium">Reply quality</th>
                <th className="px-3 py-2 font-medium">Clock-in</th>
                <th className="px-3 py-2 font-medium text-right">Clients</th>
                <th className="px-3 py-2 font-medium text-right">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ name, s, c }) => (
                <tr key={name} onClick={() => s && setOpen(name)} className={`border-b border-[#1a1a1a] last:border-0 ${s ? "cursor-pointer hover:bg-[#161616]" : ""}`}>
                  <td className="px-3 py-2.5 font-medium text-zinc-100 whitespace-nowrap">{name}</td>
                  <td className={`px-3 py-2.5 font-display text-xl font-extrabold tabular-nums ${tone(s?.score)}`}>{s?.score == null ? "—" : Math.round(s.score)}</td>
                  <td className="px-3 py-2.5">
                    <div className={tone(s?.parts?.touchpoints)}>{pct(s?.parts?.touchpoints)}</div>
                    {s?.touchpoints?.due ? <div className="text-[11px] text-zinc-500">{s.touchpoints.done} of {s.touchpoints.due} done</div> : null}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className={`flex items-center gap-1 ${tone(s?.parts?.feedback)}`}>{s?.feedback?.average ? <><Star className="h-3.5 w-3.5" />{s.feedback.average.toFixed(1)} / 5</> : "—"}</div>
                    {s?.feedback?.sent ? <div className="text-[11px] text-zinc-500">{s.feedback.answered} of {s.feedback.sent} answered</div> : null}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className={tone(s?.parts?.promptness)}>{pct(s?.parts?.promptness)}</div>
                    {s?.replies?.due ? <div className="text-[11px] text-zinc-500">{s.replies.late} late · {s.replies.missed} missed</div> : null}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className={tone(s?.parts?.quality)}>{s?.replies?.average == null ? "—" : `${s.replies.average} / 10`}</div>
                    {s?.replies?.judged ? (
                      <div className="text-[11px] text-zinc-500"><span className="text-emerald-400">{s.replies.green}</span> · <span className="text-amber-400">{s.replies.amber}</span> · <span className="text-red-400">{s.replies.red}</span></div>
                    ) : null}
                  </td>
                  <td className={`px-3 py-2.5 ${tone(s?.parts?.punctuality)}`}>{pct(s?.parts?.punctuality)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-zinc-300">{c?.total_clients ?? "—"}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-emerald-300">{c ? `${c.conversion_pct}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <CoachDays coach={open} month={month} canEdit={data.is_admin} questions={data.questions || {}} onClose={() => { setOpen(null); load(); }} />}
      {calibrate && <Calibrate onClose={() => setCalibrate(false)} />}
      {settings && <ScoreSettings onClose={() => { setSettings(false); load(); }} />}
    </div>
  );
}

/** Coaches whose score this month is under 85, for the Reports card. */
export function useLowScoreCoaches() {
  const [low, setLow] = useState([]);
  useEffect(() => {
    getCoachScores(thisMonth())
      .then((d) => setLow((d.coaches || []).filter((c) => c.score != null && c.score < 85).map((c) => ({ coach: c.coach_name, score: Math.round(c.score) }))))
      .catch(() => setLow([]));
  }, []);
  return low;
}
